/**
 * RESPONSIBILITY: the home page's welcome, drawn as the card that overlaps the hero.
 *
 * What a first-time visitor wants before they come: that they are welcome, when the
 * service is, where, and the handful of practical things that decide whether a family
 * turns up — and one button to go further.
 *
 * THE CARD RISES INTO THE HERO. It is pulled up over the bottom of the photograph, so the
 * first thing below the headline is already the practical answer. The hero leaves room
 * for it by the same amount: --hero-overlap in the stylesheet.
 *
 * Every word is the section's, including the tags and the button. The words that invite
 * sit on the left; the practical answer — the service, its time, when the doors open, and
 * the button — sits together in the navy panel, each line only when it is filled in.
 */
import type { ReactNode } from 'react';
import { Html } from '@framework/react';
import { idOf } from '@framework/core';
import type { HomeMissionSection } from '@app/sections';
import { WelcomeMark } from './WelcomeMark';
import { PlaceMark } from '../commons/PlaceMark';

const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
       aria-hidden="true" focusable="false">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/** `extra` sits under the welcome's words — the bulletin link, which is not this section's. */
export function HomeMission({ c, extra }: { c: HomeMissionSection; extra?: ReactNode }) {
  const tags = (c.tags ?? []).filter((t) => t.label?.trim());
  const hasSide = c.serviceLabel || c.serviceTime || c.serviceNote || c.ctaLabel;
  if (!c.heading && !c.bodyHtml && !c.address && !hasSide) return null;

  return (
    <section className="visit-card-section" id="meet-acfi" data-anchor="meet-acfi">
      <div className="wrap">
        <div className="visit-card">
          <div className="visit-card-main">
            {c.eyebrow && <span className="visit-card-kicker">{c.eyebrow}</span>}

            {c.heading && <h2 className="visit-card-title">{c.heading}</h2>}

            {c.address && (
              <p className="visit-card-where">
                {c.addressHref ? (
                  <a href={c.addressHref} target="_blank" rel="noopener noreferrer">
                    <PlaceMark />{c.address}
                  </a>
                ) : (
                  <><PlaceMark />{c.address}</>
                )}
              </p>
            )}

            {c.bodyHtml && <Html className="visit-card-body">{c.bodyHtml}</Html>}

            {/* Directly under the welcome's last words (James): the service panel beside
                it was too cramped for a second line of words. */}
            {extra}

            {tags.length > 0 && (
              <ul className="join-tags visit-card-tags">
                {tags.map((t) => (
                  <li className="join-tag" key={idOf(t)}>
                    <WelcomeMark name={t.icon} />
                    {t.label}
                  </li>
                ))}
              </ul>
            )}

            {c.tagline && <p className="visit-card-tagline">{c.tagline}</p>}
          </div>

          {hasSide && (
            <div className="visit-card-side">
              {c.serviceLabel && <span className="visit-card-side-label">{c.serviceLabel}</span>}
              {c.serviceTime && <span className="visit-card-side-time">{c.serviceTime}</span>}
              {c.serviceNote && <span className="visit-card-side-note">{c.serviceNote}</span>}
              {c.ctaLabel && (
                <a className="visit-card-btn" href={c.ctaHref || '#'}>{c.ctaLabel}<Arrow /></a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
